import { Inject, Injectable, NgModule, Provider } from "@angular/core";
import { DateAdapter, MAT_DATE_LOCALE, NativeDateAdapter, provideNativeDateAdapter } from "@angular/material/core";
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MatMomentDateAdapterOptions,
  MomentDateAdapter,
} from "@angular/material-moment-adapter";

import moment, { Moment } from "moment";

import { DATE_FORMATS, ELocale, LocaleService } from "./locale.service";

function getDateInputFormat(locale: string): string {
  const formats: Readonly<Record<string, string>> = DATE_FORMATS;

  return (formats[locale] ?? DATE_FORMATS[ELocale.enUS]).toUpperCase();
}

function getDateInputFormats(locale: string): string[] {
  const format = getDateInputFormat(locale);

  return [
    format,
    format.replace("DD", "D"),
    format.replace("MM", "M"),
    format.replace(/DD|MM/g, token => token[0]),
    format.replace(/[/.]/g, ""),
  ];
}

@Injectable()
export class InputMomentDateAdapter extends MomentDateAdapter {
  constructor(
    @Inject(MAT_DATE_LOCALE) locale: string,
    @Inject(MAT_MOMENT_DATE_ADAPTER_OPTIONS) options: MatMomentDateAdapterOptions,
  ) {
    super(locale, { ...options, strict: true });
  }

  public override parse(value: unknown, parseFormat: string | string[]): Moment | null {
    return typeof value === "string"
      ? super.parse(value.trim(), getDateInputFormats(this.locale))
      : super.parse(value, parseFormat);
  }

  public override format(date: Moment, displayFormat: string): string {
    return super.format(date, displayFormat.toUpperCase() === "L" ? getDateInputFormat(this.locale) : displayFormat);
  }
}

@Injectable()
export class InputNativeDateAdapter extends NativeDateAdapter {
  constructor(private readonly localeService: LocaleService) {
    super();
    this.setLocale(localeService.locale);
  }

  public override parse(value: unknown): Date | null {
    if (typeof value !== "string") {
      return super.parse(value);
    }

    const input = value.trim();
    if (!input) {
      return null;
    }

    const date = moment(input, getDateInputFormats(this.localeService.locale), "en", true);

    return date.isValid() ? this.createDate(date.year(), date.month(), date.date()) : this.invalid();
  }

  public override format(date: Date, displayFormat: Intl.DateTimeFormatOptions): string {
    return displayFormat.month === "numeric" && displayFormat.day === "numeric"
      ? moment(date).locale("en").format(getDateInputFormat(this.localeService.locale))
      : super.format(date, displayFormat);
  }
}

export function provideInputNativeDateAdapter(): Provider[] {
  return [...provideNativeDateAdapter(), { provide: DateAdapter, useClass: InputNativeDateAdapter }];
}

@NgModule({
  providers: [
    provideInputNativeDateAdapter()
  ]
})
export class InputNativeDateModule {}
